'use client'
// Types
import type { ILink } from '@/types/link'
// Components
import {
  Box,
  Typography,
  IconButton,
  SimpleTable,
  SimpleTableBody,
  SimpleTableRow,
  SimpleTableCell,
  SimpleTableHead,
  Skeleton,
} from '@/CommonComponents'
// Hooks
import { useTexts } from '@/contexts/LanguageContext'
// Constants
import { COLORS } from '@/lib/constants/colors'
// Components
import AddLinkDialog from '@/components/dialogs/NewLinkDialog'
// Hooks
import { useState } from 'react'
// Internal Types
interface IExternalLinksProps {
  links: ILink[]
  onChange: (links: ILink[]) => void
  readOnly?: boolean
  loading?: boolean
}
function ExternalLinkTable({
  links,
  onAdd,
  onChange,
  readOnly,
}: {
  links: ILink[]
  onAdd: () => void
  onChange: (links: ILink[]) => void
  readOnly?: boolean
}) {
  const { EXTERNAL_LINKS } = useTexts()
  return (
    <SimpleTable>
      <SimpleTableHead>
        <SimpleTableRow>
          <SimpleTableCell>
            <Typography variant="body2" bold>
              {EXTERNAL_LINKS}
            </Typography>
          </SimpleTableCell>
          <SimpleTableCell align="right">
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              {!readOnly && (
                <IconButton
                  sx={{
                    backgroundColor: COLORS.BACKGROUND.DEFAULT,
                    borderColor: COLORS.PRIMARY.DARK,
                    color: COLORS.PRIMARY.DARK,
                  }}
                  size="small"
                  icon="add"
                  variant="outlined"
                  onClick={onAdd}
                />
              )}
            </Box>
          </SimpleTableCell>
        </SimpleTableRow>
      </SimpleTableHead>
      <SimpleTableBody>
        {links.map((link, index) => (
          <SimpleTableRow key={crypto.randomUUID()}>
            <SimpleTableCell>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <IconButton
                  icon="link-outlined"
                  color="primary"
                  sx={{ mr: 2 }}
                  onClick={() => {
                    if (link.url.startsWith('https://') || link.url.startsWith('http://')) {
                      window.open(link.url, '_blank', 'noopener,noreferrer')
                    } else {
                      window.open(`https://${link.url}`, '_blank', 'noopener,noreferrer')
                    }
                  }}
                />
                <Typography variant="body2">{link.label}</Typography>
              </Box>
            </SimpleTableCell>
            <SimpleTableCell align="right">
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                <IconButton
                  icon="delete-outlined"
                  sx={{ color: COLORS.DANGER }}
                  onClick={() => {
                    onChange(links.filter((_, i) => i !== index))
                  }}
                />
              </Box>
            </SimpleTableCell>
          </SimpleTableRow>
        ))}
      </SimpleTableBody>
    </SimpleTable>
  )
}

export default function ExternalLinks({ links, onChange, readOnly, loading }: IExternalLinksProps) {
  const [isOpenDialog, setIsOpenDialog] = useState(false)

  return (
    <>
      {loading ? (
        <Skeleton variant="rectangular" height={100} />
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <ExternalLinkTable
            links={links}
            onAdd={() => {
              setIsOpenDialog(true)
            }}
            onChange={onChange}
            readOnly={readOnly}
          />
          <AddLinkDialog
            isOpen={isOpenDialog}
            onClose={() => {
              setIsOpenDialog(false)
            }}
            onChange={(link) => {
              onChange([...links, link])
              setIsOpenDialog(false)
            }}
          />
        </Box>
      )}
    </>
  )
}
